import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import 'boxicons';

function Banner() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const autoScrollTimerRef = useRef(null);

  const content = [
    {
      imageUrl: "media/banners/bladefall-banner.jpg",
      title: "Bladefall",
      description: "Life's a mess. But here it's next-level bonkers. Some 'genius' thought more of you was a great idea. Rogue AI agrees. Now slash (and shoot!) your way through the chaotic city, battling bosses until you confront the ultimate boss:  yourself",
      hasSteamButton: true,
      contentPosition: 'left',
      goToPage: '/bladefall'
    },
    {
      imageUrl: "media/banners/sabotage-banner.jpg",
      title: "Project: Sabotage",
      description: "Stay tuned for more info about our most ambitious project codenamed Sabotage",
      hasSteamButton: false,
      contentPosition: 'right',
      goToPage: '/sabotage'
    }
  ];

  const resetAutoScrollTimer = useCallback(() => {
    // Clear existing timer
    if (autoScrollTimerRef.current) {
      clearInterval(autoScrollTimerRef.current);
    }

    // Start a new timer only if not hovering
    if (!isHovering) {
      autoScrollTimerRef.current = setInterval(() => {
        setActiveIndex((prevIndex) => 
          (prevIndex + 1) % content.length
        );
      }, 7000);
    }
  }, [content.length, isHovering]);

  const handleKeydown = useCallback((e) => {
    if (e.key === 'ArrowLeft') {
      setActiveIndex((prevIndex) =>
        prevIndex === 0 ? content.length - 1 : prevIndex - 1
      );
      resetAutoScrollTimer();
    } else if (e.key === 'ArrowRight') {
      setActiveIndex((prevIndex) =>
        (prevIndex + 1) % content.length
      );
      resetAutoScrollTimer();
    }
  }, [content.length, resetAutoScrollTimer]);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = useCallback(() => {
    if (touchStart - touchEnd > 100) {
      // Swipe left
      setActiveIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % content.length;
        resetAutoScrollTimer();
        return newIndex;
      });
    } else if (touchStart - touchEnd < -100) {
      // Swipe right
      setActiveIndex((prevIndex) => {
        const newIndex = prevIndex === 0 ? content.length - 1 : prevIndex - 1;
        resetAutoScrollTimer();
        return newIndex;
      });
    }
  }, [touchStart, touchEnd, content.length, resetAutoScrollTimer]);

  useEffect(() => {
    // Initial timer setup
    resetAutoScrollTimer();

    // Cleanup timer on component unmount
    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
      }
    };
  }, [resetAutoScrollTimer]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);

  return (
    <section className="banner" id="banner">
      <div
        className="video-carousel"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {content.map((item, index) => (
          <div 
            key={item.title} 
            className={`carousel-item ${activeIndex === index ? 'active' : ''}`}
          >
            <div className="video-wrapper">
              <img 
                src={item.imageUrl} 
                alt={item.title} 
                className="video-background"
              />
              <div className={`banner-content ${item.contentPosition}`}>
                <div className="banner-text-container">
                  <h2>{item.title}</h2>
                  <p>{item.description}</p>
                  <div className="banner-buttons">
                    {item.hasSteamButton && (
                      <a
                        href="https://store.steampowered.com/app/3066110/Bladefall/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                      <button className="steam-btn">
                        <box-icon type='logo' name='steam'></box-icon> Wishlist on Steam
                      </button>
                      </a>
                    )}
                    {item.goToPage && (
                      <Link to={item.goToPage} className="goto-btn">
                        GO TO PAGE
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="carousel-progress">
        {content.map((item, index) => (
          <div
            key={item.title}
            className="progress-bar-container"
            onMouseEnter={() => {
              setIsHovering(true);
              setActiveIndex(index);
            }}
            onMouseLeave={() => {
              setIsHovering(false);
              resetAutoScrollTimer();
            }}
          >
            <div className={`progress-title ${activeIndex === index ? 'active' : ''}`}>
              {item.title}
            </div>
            <div className="progress-bar">
              <div
                className={`progress-fill ${activeIndex === index ? 'active' : ''}`}
                style={{
                  animationPlayState: activeIndex === index && !isHovering ? 'running' : 'paused'
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Banner;